import React, { useEffect } from 'react';

import { useAppSelector } from '../../../stores/redux.store';
import ReCAPTCHAEnterprise from '../../../components/ReCAPTCHAEnterprise';
import ReCaptchaContainer from '../../../components/ReCaptchaContainer';
import { useRecaptchaContext } from '../../../contexts/recaptcha';
import { useLoginTooManyAttemptsRecaptchaContext } from '../contexts/LoginTooManyAttemptsRecaptchaContext';
import { usePrevious } from '../../../utilities/hooks';
import { useRegisterComponent } from '../../orchestrator';

type Props = {
  /**
   * Determines which redux slice the hook selects from:
   *   'v1' -> `state.login` (this is the default if unspecified)
   *   'v2' -> `state.loginV2`
   *
   * Consumer supplies this based on whether the login page targets `POST /check-username` or `POST
   * /v2/check-username`, respectively.
   *
   * The option is there to support migration from the former to the latter.
   *
   * It may be removed when the migration is complete across all login page implemenations.
   *
   * See README-login-v2.md for details.
   */
  loginStateSource?: 'v1' | 'v2';
};

export const LoginRecaptcha = ({ loginStateSource = 'v1' }: Props) => {
  useRegisterComponent(LoginRecaptcha.name);

  const { enabled: recaptchaEnabled, component: recaptchaComponent } = useRecaptchaContext();
  const isSecondStep = useAppSelector(state =>
    loginStateSource === 'v2' ? state.loginV2?.isUsernameChecked : state.login?.isUsernameChecked
  );
  const isCaptchaRequired = useAppSelector(state =>
    loginStateSource === 'v2' ? state.loginV2?.isCaptchaRequired : state.login?.isCaptchaRequired
  );

  const shouldRenderCheckUsernameRecaptcha =
    !isSecondStep && recaptchaEnabled && recaptchaComponent;

  const { setCaptchaValue } = useLoginTooManyAttemptsRecaptchaContext();
  const shouldRenderTooManyAttemptsRecaptcha = isSecondStep && isCaptchaRequired;

  const prevIsCaptchaRequired = usePrevious(isCaptchaRequired);

  useEffect(() => {
    if (prevIsCaptchaRequired && !isCaptchaRequired) {
      setCaptchaValue(null);
    }
  }, [isCaptchaRequired, prevIsCaptchaRequired, setCaptchaValue]);

  const isCaptchaEnabledByConfig = useAppSelector(state => state.appConfig?.recaptchaEnable);
  const recaptchaEnterpriseCheckboxKeySite = useAppSelector(
    state => state.appConfig?.recaptchaEnterpriseCheckboxKeySite
  );

  return (
    <>
      {isCaptchaEnabledByConfig &&
        (shouldRenderCheckUsernameRecaptcha ? (
          <ReCaptchaContainer>{recaptchaComponent}</ReCaptchaContainer>
        ) : (
          shouldRenderTooManyAttemptsRecaptcha && (
            <ReCaptchaContainer>
              <ReCAPTCHAEnterprise
                sitekey={recaptchaEnterpriseCheckboxKeySite}
                onChange={setCaptchaValue}
              />
            </ReCaptchaContainer>
          )
        ))}
    </>
  );
};
